import type { ReactNode } from 'react'
import { useCheckoutSettings, type CheckoutSettings } from '../hooks/useCheckoutSettings.js'
import { Badge, Card, Empty, Field, Notice, PageHead, RowsSkeleton, SettingRow, Switch, Ticks } from '../components/ui.js'
import {
  BoltIcon,
  CardIcon,
  CheckoutIcon,
  LockIcon,
  MapPinIcon,
  PaletteIcon,
  PhoneIcon,
  ShieldIcon,
  StoreIcon,
  TruckIcon,
} from '../components/icons.js'

/**
 * Three screens over one settings object.
 *
 * They all read and write `/v1/checkout/settings`, which takes the whole shape
 * — so `<CheckoutFrame>` owns loading, the Save button, the dirty warning and
 * the error states, and each screen only describes its own controls.
 */
function CheckoutFrame({
  title,
  subtitle,
  crumb,
  children,
}: {
  title: string
  subtitle: string
  crumb: string
  children: (settings: CheckoutSettings, set: ReturnType<typeof useCheckoutSettings>['set']) => ReactNode
}): ReactNode {
  const { shop, settings, isPending, error, dirty, saved, saving, saveError, set, save } = useCheckoutSettings()

  if (!shop) {
    return (
      <Card>
        <Empty icon={<StoreIcon size={24} />} title="No store selected">
          Checkout settings belong to a store. Choose one from the switcher above.
        </Empty>
      </Card>
    )
  }

  return (
    <>
      <PageHead
        title={title}
        subtitle={subtitle}
        crumbs={[{ label: 'Checkout' }, { label: crumb }]}
        actions={
          <button type="button" className="btn btn--primary" disabled={!dirty || saving} onClick={save}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        }
      />

      {saved && (
        <Notice kind="ok" title="Saved">
          Your checkout is using these settings from the next session onwards.
        </Notice>
      )}

      {saveError && (
        <Notice kind="error" title="Could not save">
          {(saveError as Error).message}
        </Notice>
      )}

      {dirty && !saving && (
        <Notice kind="warn" title="Unsaved changes">
          Nothing changes for shoppers until you press Save.
        </Notice>
      )}

      {error && (
        <Card>
          <Empty error title="Could not load your checkout settings">
            {(error as Error).message}
          </Empty>
        </Card>
      )}

      {isPending && (
        <Card>
          <RowsSkeleton rows={6} />
        </Card>
      )}

      {settings && children(settings, set)}
    </>
  )
}

// ── Checkout design ─────────────────────────────────────────────────────────

export function CheckoutDesignPage(): ReactNode {
  return (
    <CheckoutFrame
      title="Checkout design"
      subtitle="Match the checkout to your brand — colours, corners and copy."
      crumb="Checkout design"
    >
      {(s, set) => (
        <div className="grid grid--main-wide">
          <div className="stack">
            <Card title="Brand" subtitle="Applied across the checkout, emails and the Buy Now button.">
              <div className="stack">
                <Field label="Brand name" hint="Shown at the top of the checkout and on order messages.">
                  <input className="input" value={s.brandName} maxLength={60} onChange={(e) => set('brandName', e.target.value)} />
                </Field>

                <Field label="Tagline" hint="A short line under the brand name.">
                  <input className="input" value={s.brandTagline} maxLength={120} onChange={(e) => set('brandTagline', e.target.value)} />
                </Field>

                <Field label="Logo URL" hint="PNG or SVG, at least 200px wide. Leave empty to show the brand name.">
                  <input className="input" value={s.brandLogo} onChange={(e) => set('brandLogo', e.target.value)} placeholder="https://…" />
                </Field>

                <Field label="Support phone" hint="Shown to shoppers if something goes wrong at checkout.">
                  <div className="input-group">
                    <PhoneIcon size={16} />
                    <input className="input" inputMode="tel" value={s.supportPhone} onChange={(e) => set('supportPhone', e.target.value)} />
                  </div>
                </Field>
              </div>
            </Card>

            <Card title="Theme">
              <div className="stack">
                <Field label="Primary colour" hint="Buttons, links and the progress bar.">
                  <div className="input-group">
                    <PaletteIcon size={16} />
                    <input
                      type="color"
                      value={s.themePrimary}
                      onChange={(e) => set('themePrimary', e.target.value)}
                      style={{ width: 40, height: 30, border: 0, background: 'none', padding: 0, cursor: 'pointer' }}
                      aria-label="Primary colour"
                    />
                    <input className="input" value={s.themePrimary} onChange={(e) => set('themePrimary', e.target.value)} />
                  </div>
                </Field>

                <Field label="Accent colour" hint="Highlights and secondary actions.">
                  <div className="input-group">
                    <PaletteIcon size={16} />
                    <input
                      type="color"
                      value={s.themeAccent}
                      onChange={(e) => set('themeAccent', e.target.value)}
                      style={{ width: 40, height: 30, border: 0, background: 'none', padding: 0, cursor: 'pointer' }}
                      aria-label="Accent colour"
                    />
                    <input className="input" value={s.themeAccent} onChange={(e) => set('themeAccent', e.target.value)} />
                  </div>
                </Field>

                <Field label="Corner radius" hint={`${s.themeRadius}px — 0 is square, 32 is fully rounded.`}>
                  <input
                    type="range"
                    min={0}
                    max={32}
                    value={s.themeRadius}
                    onChange={(e) => set('themeRadius', Number(e.target.value))}
                    style={{ accentColor: 'var(--brand)' }}
                  />
                </Field>
              </div>
            </Card>

            <Card title="Trust">
              <SettingRow title="Show trust badges" description="Secure-payment and encryption badges below the pay button.">
                <Switch checked={s.showTrustBadges} label="Show trust badges" onChange={(v) => set('showTrustBadges', v)} />
              </SettingRow>
            </Card>
          </div>

          {/* A real preview of the values in the form, not a screenshot — the
              merchant is judging the actual output. */}
          <Card title="Live preview" subtitle="How your checkout will look">
            <div
              style={{
                border: '1px solid var(--border)',
                borderRadius: s.themeRadius,
                padding: 20,
                background: 'var(--surface-2)',
                display: 'grid',
                gap: 14,
              }}
            >
              <div>
                <p style={{ fontWeight: 650, fontSize: '1rem' }}>{s.brandName || 'Your store'}</p>
                {s.brandTagline && <p className="muted" style={{ fontSize: '.8rem' }}>{s.brandTagline}</p>}
              </div>

              <div style={{ display: 'grid', gap: 8 }}>
                {['Mobile number', 'Delivery address'].map((label, index) => (
                  <div
                    key={label}
                    style={{
                      height: 38,
                      borderRadius: s.themeRadius,
                      border: '1px solid var(--border)',
                      background: 'var(--surface)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '0 12px',
                      color: 'var(--ink-faint)',
                      fontSize: '.85rem',
                    }}
                  >
                    {index === 0 ? <PhoneIcon size={15} /> : <MapPinIcon size={15} />}
                    {label}
                  </div>
                ))}
              </div>

              <div style={{ display: 'grid', gap: 6, fontSize: '.85rem' }}>
                <div className="row--between">
                  <span className="muted">Subtotal</span>
                  <span className="num">₹1,699</span>
                </div>
                {s.codEnabled && s.codFee > 0 && (
                  <div className="row--between">
                    <span className="muted">COD fee</span>
                    <span className="num">₹{s.codFee}</span>
                  </div>
                )}
                <div className="row--between" style={{ fontWeight: 650, paddingTop: 6, borderTop: '1px solid var(--border)' }}>
                  <span>Total</span>
                  <span className="num">₹{1699 + (s.codEnabled ? s.codFee : 0)}</span>
                </div>
              </div>

              <button
                type="button"
                style={{
                  background: s.themePrimary,
                  color: '#fff',
                  border: 0,
                  borderRadius: s.themeRadius,
                  padding: '11px 16px',
                  font: 'inherit',
                  fontWeight: 600,
                  cursor: 'default',
                }}
              >
                Pay ₹{1699 + (s.codEnabled ? s.codFee : 0)}
              </button>

              {s.showTrustBadges && (
                <p style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center', color: 'var(--ink-faint)', fontSize: '.76rem' }}>
                  <LockIcon size={12} /> Secured by Outfalak Checkout
                </p>
              )}
            </div>

            <p className="muted" style={{ fontSize: '.8rem', marginTop: 12 }}>
              Delivery estimate: {s.deliveryDaysMin}–{s.deliveryDaysMax} days
            </p>
          </Card>
        </div>
      )}
    </CheckoutFrame>
  )
}

// ── Buy Now button ──────────────────────────────────────────────────────────

export function BuyNowButtonPage(): ReactNode {
  return (
    <CheckoutFrame
      title="Buy Now button"
      subtitle="Put a one-click checkout button on your product pages."
      crumb="Buy Now button"
    >
      {(s, set) => (
        <div className="grid grid--main-wide">
          <div className="stack">
            <Card title="Button">
              <SettingRow title="Enable Buy Now button" description="Adds the button to every product page on this store.">
                <Switch checked={s.buyNowEnabled} label="Enable Buy Now button" onChange={(v) => set('buyNowEnabled', v)} />
              </SettingRow>

              <div className="stack" style={{ marginTop: 16, opacity: s.buyNowEnabled ? 1 : 0.5 }}>
                <Field label="Button label" hint="Keep it under 30 characters so it does not wrap on mobile.">
                  <input
                    className="input"
                    value={s.buyNowLabel}
                    maxLength={30}
                    disabled={!s.buyNowEnabled}
                    onChange={(e) => set('buyNowLabel', e.target.value)}
                  />
                </Field>
              </div>
            </Card>

            <Card title="Your store's own button">
              {/* This one edits somebody else's theme, which is where storefront
                  trouble has historically come from — so it says so. */}
              <SettingRow
                title="Hide the native buy button"
                description="Hides your platform's own dynamic checkout button so shoppers see one call to action."
              >
                <Switch
                  checked={s.hideNativeBuyButton}
                  label="Hide the native buy button"
                  disabled={!s.buyNowEnabled}
                  onChange={(v) => set('hideNativeBuyButton', v)}
                />
              </SettingRow>

              {s.hideNativeBuyButton && (
                <Notice kind="warn" title="This changes your theme">
                  Hiding the native button touches your storefront theme. If your product pages look wrong afterwards, turn this
                  back off first.
                </Notice>
              )}
            </Card>
          </div>

          <Card title="Preview" subtitle="On a product page">
            <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 18, background: 'var(--surface-2)', display: 'grid', gap: 12 }}>
              <div style={{ height: 120, borderRadius: 8, background: 'var(--surface-3)', display: 'grid', placeItems: 'center', color: 'var(--ink-faint)' }}>
                Product image
              </div>
              <strong>Premium Cotton T-Shirt</strong>
              <span className="num" style={{ fontSize: '1.15rem', fontWeight: 650 }}>
                ₹1,499
              </span>

              {!s.hideNativeBuyButton && (
                <button type="button" className="btn btn--block" style={{ cursor: 'default' }}>
                  Add to cart
                </button>
              )}

              {s.buyNowEnabled && (
                <button
                  type="button"
                  className="btn btn--block"
                  style={{ background: s.themePrimary, borderColor: s.themePrimary, color: '#fff', cursor: 'default' }}
                >
                  <BoltIcon size={16} />
                  {s.buyNowLabel || 'Buy Now'}
                </button>
              )}
            </div>

            <Ticks
              brand
              items={['Skips the cart entirely', 'Pre-fills a returning shopper’s address', 'UPI, cards, netbanking and COD']}
            />
          </Card>
        </div>
      )}
    </CheckoutFrame>
  )
}

// ── COD rules ───────────────────────────────────────────────────────────────

export function CodRulesPage(): ReactNode {
  return (
    <CheckoutFrame title="COD rules" subtitle="Decide who can pay cash on delivery, and what it costs." crumb="COD rules">
      {(s, set) => (
        <div className="grid grid--main-wide">
          <div className="stack">
            <Card title="Availability">
              <SettingRow title="Offer Cash on Delivery" description="Turning this off makes every order prepaid.">
                <Switch checked={s.codEnabled} label="Offer Cash on Delivery" onChange={(v) => set('codEnabled', v)} />
              </SettingRow>

              <div className="grid grid--2" style={{ marginTop: 16, opacity: s.codEnabled ? 1 : 0.5 }}>
                <Field label="COD fee (₹)" hint="Added to the order total when COD is chosen.">
                  <input
                    className="input"
                    inputMode="decimal"
                    value={String(s.codFee)}
                    disabled={!s.codEnabled}
                    onChange={(e) => set('codFee', Number(e.target.value.replace(/[^\d.]/g, '')) || 0)}
                  />
                </Field>

                <Field label="Maximum order value (₹)" hint="Above this, only prepaid is offered.">
                  <input
                    className="input"
                    inputMode="decimal"
                    value={String(s.codMaxOrderValue)}
                    disabled={!s.codEnabled}
                    onChange={(e) => set('codMaxOrderValue', Number(e.target.value.replace(/[^\d.]/g, '')) || 0)}
                  />
                </Field>
              </div>
            </Card>

            <Card title="Verification">
              <SettingRow
                title="OTP verification"
                description="Managed by Outfalak Checkout for every store. Contact support to change it."
              >
                <Badge tone={s.otpEnabled ? 'ok' : 'warn'}>{s.otpEnabled ? 'On' : 'Off'}</Badge>
              </SettingRow>

              {!s.otpEnabled && s.codEnabled && (
                <Notice kind="warn" title="COD without verification">
                  Unverified COD orders return far more often. Contact support if you want OTP switched on.
                </Notice>
              )}
            </Card>

            <Card title="Marketing consent">
              <SettingRow
                title="Ask shoppers before sharing their details"
                description="Your marketing tools — Facebook, GA4, MoEngage, Wigzo, CleverTap — receive every sale either way. This decides whether they also get the shopper's email, phone and name."
              >
                <Switch
                  checked={s.trackingRequiresConsent}
                  label="Ask shoppers before sharing their details"
                  onChange={(v) => set('trackingRequiresConsent', v)}
                />
              </SettingRow>

              {s.trackingRequiresConsent ? (
                <Field
                  label="What the shopper agrees to"
                  hint="Shown beside a checkbox on the payment step. Unticked by default — a pre-ticked box is not consent."
                >
                  <input
                    className="input"
                    value={s.trackingConsentText}
                    onChange={(e) => set('trackingConsentText', e.target.value)}
                  />
                </Field>
              ) : (
                <Notice kind="warn" title="Nobody is being asked">
                  Every shopper's email, phone and name goes to your marketing tools whether they agreed or not. Only
                  switch this off if you have your own lawful basis for it — it is your decision to defend, not ours.
                </Notice>
              )}
            </Card>

            <Card title="Shopper convenience">
              <SettingRow title="Address autofill" description="Pre-fill saved addresses for returning shoppers.">
                <Switch checked={s.autofillEnabled} label="Address autofill" onChange={(v) => set('autofillEnabled', v)} />
              </SettingRow>
              <SettingRow title="Save customer details" description="Remember a shopper's address for their next order.">
                <Switch checked={s.saveCustomerDetails} label="Save customer details" onChange={(v) => set('saveCustomerDetails', v)} />
              </SettingRow>
            </Card>

            <Card title="Delivery estimate" subtitle="Shown to shoppers at checkout.">
              <div className="grid grid--2">
                <Field label="Fastest (days)">
                  <input
                    className="input"
                    inputMode="numeric"
                    value={String(s.deliveryDaysMin)}
                    onChange={(e) => set('deliveryDaysMin', Number(e.target.value.replace(/\D/g, '')) || 0)}
                  />
                </Field>
                <Field label="Slowest (days)">
                  <input
                    className="input"
                    inputMode="numeric"
                    value={String(s.deliveryDaysMax)}
                    onChange={(e) => set('deliveryDaysMax', Number(e.target.value.replace(/\D/g, '')) || 0)}
                  />
                </Field>
              </div>
              {s.deliveryDaysMax < s.deliveryDaysMin && (
                <Notice kind="error">The slowest estimate cannot be shorter than the fastest — saving will be refused.</Notice>
              )}
            </Card>
          </div>

          <div className="stack">
            <Card title="What the shopper sees" accent>
              <p className="muted" style={{ fontSize: '.87rem', marginBottom: 12 }}>
                On a ₹1,499 order, these payment options are offered:
              </p>
              <ul className="list list--plain">
                <li style={{ paddingLeft: 0 }}>
                  <span className="feature__icon" style={{ ['--tone' as string]: 'var(--ok)' }}>
                    <CardIcon size={16} />
                  </span>
                  <span className="list__text">
                    <strong>Pay online</strong>
                    <small>UPI, cards, netbanking, wallets</small>
                  </span>
                </li>
                <li style={{ paddingLeft: 0 }}>
                  <span className="feature__icon" style={{ ['--tone' as string]: s.codEnabled ? 'var(--warn)' : 'var(--ink-faint)' }}>
                    <TruckIcon size={16} />
                  </span>
                  <span className="list__text">
                    <strong>{s.codEnabled ? `Cash on Delivery${s.codFee > 0 ? ` + ₹${s.codFee}` : ''}` : 'Cash on Delivery (off)'}</strong>
                    <small>{s.codEnabled ? (s.otpEnabled ? 'OTP verification required' : 'No verification') : 'Every order is prepaid'}</small>
                  </span>
                </li>
              </ul>
            </Card>

            <Card title="Current rules">
              <ul className="list list--plain">
                <li style={{ paddingLeft: 0 }}>
                  <span className="list__text">
                    <strong>COD</strong>
                    <small>{s.codEnabled ? `Offered up to ₹${s.codMaxOrderValue.toLocaleString('en-IN')}` : 'Not offered'}</small>
                  </span>
                  <Badge tone={s.codEnabled ? 'ok' : undefined}>{s.codEnabled ? 'On' : 'Off'}</Badge>
                </li>
                <li style={{ paddingLeft: 0 }}>
                  <span className="list__text">
                    <strong>OTP</strong>
                    <small>{s.otpEnabled ? 'Every order verified' : 'Not required'}</small>
                  </span>
                  <Badge tone={s.otpEnabled ? 'ok' : 'warn'}>{s.otpEnabled ? 'On' : 'Off'}</Badge>
                </li>
              </ul>
            </Card>

            <Card title="Why this matters">
              <Ticks
                items={[
                  'COD orders return more often than prepaid ones.',
                  'OTP verification cuts fake orders sharply.',
                  'A small COD fee pushes shoppers toward prepaid.',
                ]}
              />
            </Card>
          </div>
        </div>
      )}
    </CheckoutFrame>
  )
}

// ── Checkout settings ───────────────────────────────────────────────────────

export function CheckoutSettingsPage(): ReactNode {
  return (
    <CheckoutFrame
      title="Checkout settings"
      subtitle="How the checkout behaves — verification, saved details and trust."
      crumb="Checkout settings"
    >
      {(s, set) => (
        <div className="grid grid--2">
          <Card title="Payment">
            <SettingRow title="Cash on Delivery" description="Offer COD alongside online payment.">
              <Switch checked={s.codEnabled} label="Cash on Delivery" onChange={(v) => set('codEnabled', v)} />
            </SettingRow>
            <SettingRow title="Show trust badges" description="Secure-payment badges below the pay button.">
              <Switch checked={s.showTrustBadges} label="Show trust badges" onChange={(v) => set('showTrustBadges', v)} />
            </SettingRow>
            <SettingRow title="Buy Now button" description="One-click checkout on product pages.">
              <Switch checked={s.buyNowEnabled} label="Buy Now button" onChange={(v) => set('buyNowEnabled', v)} />
            </SettingRow>
          </Card>

          <Card title="Status">
            <ul className="list list--plain">
              <li style={{ paddingLeft: 0 }}>
                <span className="feature__icon" style={{ ['--tone' as string]: 'var(--ok)' }}>
                  <CheckoutIcon size={16} />
                </span>
                <span className="list__text">
                  <strong>Checkout is live</strong>
                  <small>Serving your storefront right now</small>
                </span>
                <Badge tone="ok" dot>
                  Active
                </Badge>
              </li>
              <li style={{ paddingLeft: 0 }}>
                <span className="feature__icon" style={{ ['--tone' as string]: 'var(--brand)' }}>
                  <ShieldIcon size={16} />
                </span>
                <span className="list__text">
                  <strong>PCI-compliant payments</strong>
                  <small>Card details never touch your servers</small>
                </span>
                <Badge tone="ok">Verified</Badge>
              </li>
            </ul>
          </Card>

          <Card title="Brand">
            <dl className="kv">
              <div className="kv__row">
                <dt>Brand name</dt>
                <dd>{s.brandName || '—'}</dd>
              </div>
              <div className="kv__row">
                <dt>Primary colour</dt>
                <dd>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 12,
                      height: 12,
                      borderRadius: 3,
                      background: s.themePrimary,
                      verticalAlign: '-1px',
                      marginRight: 6,
                    }}
                  />
                  {s.themePrimary}
                </dd>
              </div>
              <div className="kv__row">
                <dt>Corner radius</dt>
                <dd>{s.themeRadius}px</dd>
              </div>
              <div className="kv__row">
                <dt>Delivery estimate</dt>
                <dd>
                  {s.deliveryDaysMin}–{s.deliveryDaysMax} days
                </dd>
              </div>
            </dl>
          </Card>
        </div>
      )}
    </CheckoutFrame>
  )
}
